import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import type {ExtendedItemProps, SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {ItemPickerLabelBase, type ItemPickerLabelItem} from '@github-ui/item-picker-label'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {IS_SERVER} from '@github-ui/ssr-utils'
import type React from 'react'
import {type MutableRefObject, type RefObject, useCallback, useEffect, useMemo, useRef, useState} from 'react'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {fetchQuery, graphql, readInlineData, useClientQuery, useFragment, useRelayEnvironment} from 'react-relay'

import type {
  ItemPickerLabelClientQuery,
  ItemPickerLabelClientQuery$data,
} from './__generated__/ItemPickerLabelClientQuery.graphql'
import type {ItemPickerLabelItem$data, ItemPickerLabelItem$key} from './__generated__/ItemPickerLabelItem.graphql'
import type {ItemPickerLabelItemsPaginated$key} from './__generated__/ItemPickerLabelItemsPaginated.graphql'
import type {ItemPickerLabelPermissionQuery} from './__generated__/ItemPickerLabelPermissionQuery.graphql'
import type {ItemPickerLabelQuery} from './__generated__/ItemPickerLabelQuery.graphql'
import type {
  ItemPickerLabelSearchQuery,
  ItemPickerLabelSearchQuery$data,
} from './__generated__/ItemPickerLabelSearchQuery.graphql'
import type {updateItemPickerIssueLabelsBulkByQueryMutation$data} from './mutations/__generated__/updateItemPickerIssueLabelsBulkByQueryMutation.graphql'
import type {updateItemPickerIssueLabelsBulkMutation$data} from './mutations/__generated__/updateItemPickerIssueLabelsBulkMutation.graphql'
import {commitCreateLabelMutation} from './mutations/create-item-picker-label-mutation'
import {commitUpdateItemPickerIssueLabelsBulkByQueryMutation} from './mutations/update-item-picker-issue-labels-bulk-by-query-mutation'
import {commitUpdateItemPickerIssueLabelsBulkMutation} from './mutations/update-item-picker-issue-labels-bulk-mutation'

//
// ItemPickerLabelBase <== ItemPickerWrapper (internal) <== ItemPickerFetcher (internal) <== ItemPickerLabel (exported)
//                                                                              <== BulkItemPickerLabel (exported)

export type ItemPickerLabelRelayItem = Omit<ItemPickerLabelItem$data, ' $fragmentType'>

export function readLabelFromFragment(key: ItemPickerLabelItem$key): ItemPickerLabelRelayItem {
  // eslint-disable-next-line no-restricted-syntax
  return readInlineData<ItemPickerLabelItem$key>(LabelFragment, key)
}

export type ItemPickerLabelProps = {
  repo: string
  owner: string
  readonly: boolean
  labels: ItemPickerLabelRelayItem[]
  copilotSuggestedLabels?: ItemPickerLabelRelayItem[]
  favoriteLabelIds?: string[]
  labelNames?: string[]
  insidePortal?: boolean
  shortcutEnabled: boolean
  noLabelOption?: ExtendedItemProps<ItemPickerLabelRelayItem>
  anchorElement: (props: React.HTMLAttributes<HTMLElement>, ref: RefObject<HTMLButtonElement>) => React.ReactElement
  onSelectionChanged: (selectedItems: ItemPickerLabelRelayItem[]) => void
  /**
   * Whether to render the label picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  triggerOpen?: boolean
  initialFilter?: string
  title?: string
  showEditLabelsButton?: boolean
  showNoMatchItem?: boolean
}

type BulkItemPickerLabelProps = Omit<ItemPickerLabelProps, 'onSelectionChange' | 'labels'> & {
  existingIssueLabels: Map<string, string[]>
  labelAppliedToAll: ItemPickerLabelRelayItem[]
  connectionIds: {[key: string]: string[]}
} & SharedBulkActionsItemPickerProps

type ItemPickerWrapperProps = Omit<ItemPickerLabelProps, 'labelQueryRef'> & {
  paginatedKey: ItemPickerLabelItemsPaginated$key | null
  isLoading: boolean
  setIsLoading: (isLoading: boolean) => void
  copilotSuggestedLabels?: ItemPickerLabelRelayItem[]

  /**
   * Callback to search for labels on the server.
   *
   * Only used when repository has more than VALUES.labelsInitialLoadCount
   *
   * @param query filter query
   * @returns Search results
   */
  searchLabels?: (query: string) => Promise<ItemPickerLabelRelayItem[]>
  repoNwo?: string
  canCreateLabel: MutableRefObject<boolean>
  preloadedData?: ItemPickerLabelClientQuery$data['repository'] | null
}

export const ItemPickerLabelGraphqlQuery = graphql`
  query ItemPickerLabelQuery(
    $owner: String!
    $repo: String!
    $shouldQueryByNames: Boolean = false
    $labelNames: String = ""
    $count: Int = 100
  ) {
    repository(owner: $owner, name: $repo) {
      viewerIssueCreationPermissions {
        labelable
      }
      ...ItemPickerLabelItemsPaginated
      labelsByNames: labels(names: $labelNames, first: $count, orderBy: {field: NAME, direction: ASC})
        @include(if: $shouldQueryByNames) {
        nodes {
          ...ItemPickerLabelItem
        }
      }
    }
  }
`

const ItemPickerLabelPermissionGraphqlQuery = graphql`
  query ItemPickerLabelPermissionQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      viewerIssueCreationPermissions {
        labelable
      }
    }
  }
`

const ItemPickerLabelSearchGraphqlQuery = graphql`
  query ItemPickerLabelSearchQuery($owner: String!, $repo: String!, $query: String, $count: Int!) {
    repository(owner: $owner, name: $repo) {
      labels(query: $query, first: $count) {
        nodes {
          ...ItemPickerLabelItem
        }
      }
    }
  }
`

export const AssignedLabelFragment = graphql`
  fragment ItemPickerLabelAssignedLabels on Labelable
  @argumentDefinitions(cursor: {type: "String"}, assignedLabelPageSize: {type: "Int", defaultValue: 100})
  @refetchable(queryName: "ItemPickerLabelAssignedLabelsQuery") {
    labels(first: $assignedLabelPageSize, after: $cursor, orderBy: {field: NAME, direction: ASC})
      @connection(key: "ItemPickerLabel_labels") {
      # eslint-disable-next-line relay/unused-fields
      edges {
        node {
          ...ItemPickerLabelItem
        }
      }
    }
  }
`

const ItemPickerLabelClientGraphqlQuery = graphql`
  query ItemPickerLabelClientQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      id
      labels {
        # eslint-disable-next-line relay/unused-fields
        totalCount
        nodes {
          ...ItemPickerLabelItem
        }
      }
    }
  }
`

export const ItemPickerLabelItemsPaginatedFragment = graphql`
  fragment ItemPickerLabelItemsPaginated on Repository
  @argumentDefinitions(cursor: {type: "String"}, count: {type: "Int", defaultValue: 100})
  @refetchable(queryName: "ItemPickerLabelsListPaginationQuery") {
    id
    labels(first: $count, after: $cursor, orderBy: {field: NAME, direction: ASC}) {
      nodes {
        ...ItemPickerLabelItem
      }
      # eslint-disable-next-line relay/unused-fields
      totalCount
    }
  }
`

export const LabelFragment = graphql`
  fragment ItemPickerLabelItem on Label @inline {
    id
    # eslint-disable-next-line relay/unused-fields
    color
    name
    # eslint-disable-next-line relay/unused-fields
    nameHTML
    # eslint-disable-next-line relay/unused-fields
    description
    # eslint-disable-next-line relay/unused-fields
    url
  }
`

export function BulkIssuesItemPickerLabel({
  issuesToActOn,
  existingIssueLabels,
  labelAppliedToAll,
  connectionIds,
  useQueryForAction,
  repositoryId,
  query,
  onCompleted,
  onError,
  ...props
}: Omit<BulkItemPickerLabelProps, 'onSelectionChanged'>) {
  const environment = useRelayEnvironment()

  const onSelectionChange = useCallback(
    (selectedLabels: ItemPickerLabelRelayItem[]) => {
      const labelsToAdd = selectedLabels.filter(l => !labelAppliedToAll.some(la => la.id === l.id))
      const labelsToRemove = labelAppliedToAll.filter(la => !selectedLabels.some(l => la.id === l.id))
      const args = {
        applyLabelIds: labelsToAdd.map(l => l.id),
        removeLabelIds: labelsToRemove.map(l => l.id),
      }
      const mutationArgs = {
        environment,
        optimisticUpdateIds: issuesToActOn,
        existingIssueLabels,
        connectionIds,
        onError: (error: Error) => {
          onError?.(error)
        },
      }

      if (useQueryForAction && repositoryId && query) {
        commitUpdateItemPickerIssueLabelsBulkByQueryMutation({
          ...mutationArgs,
          input: {
            repositoryId,
            query,
            ...args,
          },
          onCompleted: ({updateIssuesBulkByQuery}: updateItemPickerIssueLabelsBulkByQueryMutation$data) => {
            onCompleted?.(updateIssuesBulkByQuery?.jobId || undefined)
          },
        })
      } else {
        commitUpdateItemPickerIssueLabelsBulkMutation({
          ...mutationArgs,
          input: {
            ids: [...issuesToActOn],
            ...args,
          },
          onCompleted: ({updateIssuesBulk}: updateItemPickerIssueLabelsBulkMutation$data) => {
            onCompleted?.(updateIssuesBulk?.jobId || undefined)
          },
        })
      }
    },
    [
      connectionIds,
      environment,
      existingIssueLabels,
      issuesToActOn,
      labelAppliedToAll,
      onCompleted,
      onError,
      query,
      repositoryId,
      useQueryForAction,
    ],
  )

  return (
    <ItemPickerLabel
      labels={labelAppliedToAll}
      onSelectionChanged={onSelectionChange}
      showEditLabelsButton={false}
      {...props}
    />
  )
}

export function ItemPickerLabel({shortcutEnabled, anchorElement, showEditLabelsButton, ...rest}: ItemPickerLabelProps) {
  return (
    <LazyItemPicker
      anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
      createChild={() => (
        <ItemPickerFetcher
          anchorElement={anchorElement}
          shortcutEnabled={shortcutEnabled}
          showEditLabelsButton={showEditLabelsButton}
          {...rest}
        />
      )}
      insidePortal={rest.insidePortal}
      keybindingCommandId="item-pickers:open-labels"
    />
  )
}

function ItemPickerFetcher({
  repo,
  owner,
  labels,
  labelNames,
  onSelectionChanged: onSelectionChange,
  ...props
}: ItemPickerLabelProps) {
  const environment = useRelayEnvironment()

  const [isLoading, setIsLoading] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<ItemPickerLabelItemsPaginated$key | null>(null)
  const [initialLabels, setInitialLabels] = useState<ItemPickerLabelRelayItem[]>(labels)
  const repoNwo = `${owner}/${repo}`
  const preloadedData = useClientQuery<ItemPickerLabelClientQuery>(ItemPickerLabelClientGraphqlQuery, {
    owner,
    repo,
  })
  const hasPreloadedData = Boolean(preloadedData?.repository?.labels?.nodes?.length)
  const canCreateLabel = useRef(false)

  useEffect(() => {
    if (!IS_SERVER) {
      if (hasPreloadedData) {
        clientSideRelayFetchQueryRetained<ItemPickerLabelPermissionQuery>({
          environment,
          query: ItemPickerLabelPermissionGraphqlQuery,
          variables: {
            owner,
            repo,
          },
        }).subscribe({
          next: response => {
            canCreateLabel.current = response.repository?.viewerIssueCreationPermissions?.labelable || false
            setIsLoading(false)
            setIsError(false)
          },
          error: () => {
            setIsError(true)
          },
        })

        if (labelNames) {
          const labelNamesStrippedQuotes = labelNames.map(name => name.replace(/^"/, '').replace(/"$/, ''))
          const labelsByName = (preloadedData.repository?.labels?.nodes || []).flatMap(node => {
            if (!node) return []
            // eslint-disable-next-line no-restricted-syntax
            const label = readInlineData<ItemPickerLabelItem$key>(LabelFragment, node)
            return labelNamesStrippedQuotes.includes(label.name) ? [label] : []
          })
          setInitialLabels(labelsByName)
        }
      } else {
        clientSideRelayFetchQueryRetained<ItemPickerLabelQuery>({
          environment,
          query: ItemPickerLabelGraphqlQuery,
          variables: {
            owner,
            repo,
            // Used on issues#index for fetching the labels present in the query string
            shouldQueryByNames: labelNames && labelNames.length > 0,
            labelNames: labelNames?.join(','),
            count: VALUES.labelsInitialLoadCount,
          },
        }).subscribe({
          next: response => {
            const repoLabels = response.repository
            canCreateLabel.current = response.repository?.viewerIssueCreationPermissions?.labelable || false
            setData(repoLabels ?? null)
            if (labelNames && response.repository?.labelsByNames) {
              const labelsByName = (response.repository?.labelsByNames.nodes || []).flatMap(node =>
                // eslint-disable-next-line no-restricted-syntax
                node ? [readInlineData<ItemPickerLabelItem$key>(LabelFragment, node)] : [],
              )
              setInitialLabels(labelsByName)
            }
            setIsLoading(false)
            setIsError(false)
          },
          error: () => {
            setIsError(true)
          },
        })
      }
    }
  }, [environment, fetchKey, labelNames, owner, repo, hasPreloadedData, preloadedData])

  const searchLabels = useCallback(
    (query: string) =>
      new Promise<ItemPickerLabelRelayItem[]>((resolve, reject) => {
        fetchQuery<ItemPickerLabelSearchQuery>(environment, ItemPickerLabelSearchGraphqlQuery, {
          query,
          owner,
          repo,
          count: VALUES.labelsPageSize,
        }).subscribe({
          next: (fetchedData: ItemPickerLabelSearchQuery$data) => {
            if (fetchedData !== null) {
              const suggestedLabels = (fetchedData.repository?.labels?.nodes || []).flatMap(
                // eslint-disable-next-line no-restricted-syntax
                node => (node ? [readInlineData<ItemPickerLabelItem$key>(LabelFragment, node)] : []),
              )

              resolve(suggestedLabels)
            }
          },
          error: (error: Error) => {
            reject(error)
          },
        })
      }),
    [environment, owner, repo],
  )

  const anchorRef = useRef<HTMLButtonElement>(null)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('labels'),
    anchorElement: anchorProps => props.anchorElement(anchorProps, anchorRef),
    open: true,
  })

  const allLabels = useMemo(() => {
    const a = labelNames ? initialLabels : labels
    if (props.noLabelOption?.selected && !a.find(l => l.id === SPECIAL_VALUES.noLabelsData.id)) {
      a.unshift(SPECIAL_VALUES.noLabelsData as ItemPickerLabelRelayItem)
    }
    return a
  }, [initialLabels, labelNames, labels, props.noLabelOption?.selected])

  if (isError) {
    return createFallbackComponent(() => setFetchKey(fetchKey + 1))
  }

  return (
    <ItemPickerWrapper
      repo={repo}
      owner={owner}
      paginatedKey={data}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      labels={allLabels}
      onSelectionChanged={onSelectionChange}
      searchLabels={searchLabels}
      repoNwo={repoNwo}
      preloadedData={hasPreloadedData ? preloadedData?.repository : undefined}
      canCreateLabel={canCreateLabel}
      {...props}
    />
  )
}

function ItemPickerWrapper({
  labels,
  copilotSuggestedLabels,
  favoriteLabelIds,
  searchLabels,
  paginatedKey,
  isLoading,
  setIsLoading,
  onSelectionChanged: onSelectionChange,
  insidePortal,
  anchorElement,
  nested = false,
  triggerOpen = true,
  initialFilter,
  repoNwo,
  showEditLabelsButton = true,
  noLabelOption = undefined,
  title,
  showNoMatchItem = false,
  preloadedData,
  canCreateLabel,
  repo,
  owner,
  readonly,
  shortcutEnabled,
}: ItemPickerWrapperProps) {
  const data = useFragment(ItemPickerLabelItemsPaginatedFragment, paginatedKey)
  const repositoryId = preloadedData?.id || data?.id
  const totalNumberOfLabels = preloadedData?.labels?.totalCount || data?.labels?.totalCount || 0
  const fetchedLabels: ItemPickerLabelItem[] = useMemo(() => {
    const labelItems: ItemPickerLabelItem[] = (preloadedData?.labels?.nodes || data?.labels?.nodes || []).flatMap(a => {
      if (!a) return []
      // eslint-disable-next-line no-restricted-syntax
      const labelData = readInlineData<ItemPickerLabelItem$key>(LabelFragment, a)
      return [labelData]
    })

    if (noLabelOption) {
      labelItems.unshift(SPECIAL_VALUES.noLabelsData as ItemPickerLabelItem)
    }

    return labelItems
  }, [data, noLabelOption, preloadedData])

  const environment = useRelayEnvironment()

  const onCreate = useCallback(
    (
      variables: {repositoryId: string; color: string; name: string},
      options: {onSuccess: (response: ItemPickerLabelRelayItem | null) => void; onError: () => void},
    ) => {
      if (!repositoryId) return

      commitCreateLabelMutation({
        environment,
        input: variables,
        onError: options.onError,
        onCompleted: response => {
          const newLabel = response?.createLabel?.label
          if (newLabel) {
            options.onSuccess(newLabel as ItemPickerLabelRelayItem)
          } else {
            options.onSuccess(null)
          }
        },
      })
    },
    [environment, repositoryId],
  )

  return (
    <ItemPickerLabelBase
      selectedLabels={labels}
      suggestions={fetchedLabels}
      totalNumberOfLabels={totalNumberOfLabels}
      copilotSuggestedLabels={copilotSuggestedLabels}
      favoriteLabelIds={favoriteLabelIds}
      searchLabels={searchLabels}
      onSelectionChange={onSelectionChange}
      insidePortal={insidePortal}
      anchorElement={anchorElement}
      nested={nested}
      triggerOpen={triggerOpen}
      initialFilter={initialFilter}
      repoNwo={repoNwo}
      showEditLabelsButton={showEditLabelsButton}
      noLabelOption={noLabelOption}
      title={title}
      showNoMatchItem={showNoMatchItem}
      canCreateLabel={canCreateLabel}
      repositoryId={repositoryId}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      onCreate={onCreate}
      repo={repo}
      owner={owner}
      readonly={readonly}
      shortcutEnabled={shortcutEnabled}
    />
  )
}

try{ BulkIssuesItemPickerLabel.displayName ||= 'BulkIssuesItemPickerLabel' } catch {}
try{ ItemPickerLabel.displayName ||= 'ItemPickerLabel' } catch {}
try{ ItemPickerFetcher.displayName ||= 'ItemPickerFetcher' } catch {}
try{ ItemPickerWrapper.displayName ||= 'ItemPickerWrapper' } catch {}